import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useCallback, useState } from "react";
import { Box, Paper, Typography, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDate } from "@internationalized/date";
import { CircleDot, Users, Database, HandCoins, CloudDownload, Plus } from "lucide-react";
import { selectCanManagePublisherReportingPlacements } from "../../redux/authSlice";
import { fetchPublisherReporting, setFilters, fetchReportingFilterOptions, fetchReportingCategories, setPagination } from "../../redux/publisherReportingSlice";
import { fetchAllClientList, fetchDesignationWiseUsers, selectAllClientList, selectDesignationWiseUsers } from "../../redux/clientDataSlice";
// Components
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import { Dropdown } from "../../common_components/FormInputs";
import TableToolbar from "../../common_components/TableToolbar";
import IOSSwitch from "../../common_components/IOSSwitch";
import { ToggleTooltipContent } from "../AffiliateAccountPerformance/components/ToggleTooltipContent";
import CommonTable from "../../common_components/CommonTable";
import Pagination from "../../common_components/Pagination";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import InfoTooltip from "../../common_components/InfoTooltip";
import { VarianceBadge } from "../../common_components/VarianceBadge";
import MetricCard from "../../common_components/MetricCard";
import AppButton from "../../common_components/AppButton";
import ActiveClientsDrawer from "./components/ActiveClientsDrawer";
import AddPlacementDrawer from "./components/AddPlacementDrawer";
import CommonTabs from "../../common_components/CommonTabs";
// Utils
import { ApiCall } from "../../helper/axios";
import { toast } from "../../common_components/Toast/ToastUtils";
import exportPublisherReportingExcel from "./exportPublisherReportingExcel";
import { truncateName } from "../../helper/truncateName";
import { NETWORK_ICON_MAP } from "../../utils/networkConstants";
import { useLayout } from "../../contexts/LayoutContext";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../utils/designationFilters";
const TAG_FILTER_OPTIONS = [
    { value: "All", label: "All" },
    { value: "TOF", label: "TOF" },
    { value: "MOF", label: "MOF" },
    { value: "BOF", label: "BOF" },
    { value: "No Tag", label: "No Tag" },
    { value: "SPAM", label: "SPAM" },
];
// Toolbar Funnel filter (By Publisher tab): "" = All like the other toolbar filters, then the funnel stages + SPAM
// ("No Tag" stays a column filter option only). The redux `funnel` value for All is "All".
const FUNNEL_FILTER_OPTIONS = [
    { value: "", label: "All" },
    ...TAG_FILTER_OPTIONS.filter((o) => o.value !== "All" && o.value !== "No Tag"),
];
const NETWORK_ICONS = {
    ...NETWORK_ICON_MAP,
    commision_factory: NETWORK_ICON_MAP.commission_factory,
};
const PERFORMANCE_TABS = [
    { label: "By Publisher", value: "publisher" },
    { label: "By Client", value: "client" },
];
const PERFORMANCE_TITLES = {
    publisher: "Publisher Performance",
    client: "Client Performance",
};
// Top summary cards: title / tooltip / icon here, the values come from the list API summary
const SUMMARY_CARDS = [
    {
        key: "placements",
        title: "Placements",
        tooltipText: "Total number of placements in the selected date range.",
        icon: _jsx(CircleDot, { size: 16 }),
    },
    {
        key: "activePublishers",
        title: "Active Publishers",
        tooltipText: "Publishers with at least one placement linked to them in the selected date range.",
        icon: _jsx(Users, { size: 16 }),
    },
    {
        key: "revenue",
        title: "Revenue",
        tooltipText: "Total revenue generated by publishers in the selected date range.",
        icon: _jsx(Database, { size: 16 }),
        prefix: "$ ",
    },
    {
        key: "spend",
        title: "Spend",
        tooltipText: "Total spend on publishers in the selected date range.",
        icon: _jsx(HandCoins, { size: 16 }),
        prefix: "$ ",
    },
];
const fmt = (num) => num?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || "0";
const fmtCurr = (num) => num?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00";
const fmtRatio = (num) => (num || 0).toFixed(2);
const MetricCompareCell = ({ value, compareValue, diff, isCurrency = false, isRatio = false, bold = false, onClick }) => {
    const clickableSx = onClick
        ? { cursor: "pointer", borderBottom: "2px dotted #181d27", pb: "1px", display: "inline-block", width: "fit-content" }
        : {};
    const formattedVal = isCurrency ? (_jsxs("span", { children: [_jsx("span", { style: { color: "#98A2B3" }, children: "$ " }), fmtCurr(value)] })) : isRatio ? (fmtRatio(value)) : (fmt(value));
    const hasCompare = compareValue !== undefined && diff !== undefined;
    if (!hasCompare) {
        return (_jsx(Box, { sx: { width: "100%", display: "flex", justifyContent: "flex-start" }, children: _jsx(Typography, { className: "all-Font-Sizes", onClick: onClick, sx: {
                    fontSize: "14px !important",
                    color: "#101828",
                    fontWeight: bold ? 700 : 400,
                    textAlign: "left",
                    width: onClick ? "fit-content" : "100%",
                    ...clickableSx,
                }, children: formattedVal }) }));
    }
    const formattedCompVal = isCurrency ? (_jsxs("span", { children: [_jsx("span", { style: { color: "#98A2B3" }, children: "$ " }), fmtCurr(compareValue ?? 0)] })) : isRatio ? (fmtRatio(compareValue ?? 0)) : (fmt(compareValue ?? 0));
    return (_jsxs(Box, { sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 0.5,
        }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start" }, children: [_jsx(Typography, { className: "all-Font-Sizes", onClick: onClick, sx: {
                            fontSize: "14px !important",
                            color: "#101828",
                            fontWeight: bold ? 700 : 500,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            ...clickableSx,
                        }, children: formattedVal }), _jsx(Typography, { className: "all-Font-Sizes", sx: {
                            fontSize: "12px !important",
                            color: "#667085",
                            fontWeight: 400,
                            lineHeight: 1.2,
                            mt: "2px",
                            whiteSpace: "nowrap",
                        }, children: formattedCompVal })] }), _jsx(VarianceBadge, { value: diff ?? 0, bold: bold, fontSize: "11px" })] }));
};
const PublisherReportingPage = () => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth?.token) || "";
    // Add Published Article needs the "Manage Placements" action of Publisher Reporting (Roles & Permission)
    const canManagePlacements = useSelector(selectCanManagePublisherReportingPlacements);
    // Collapse the sidebar on page load (same as Marketing Calendar) to give the table more room
    const { setIsSidebarExpanded } = useLayout();
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    const { view: performanceView, rowsView, rows, total, summary, loading, startDate, endDate, compareStartDate, compareEndDate, funnel, groupPublisherIds, clientId: clientFilter, accountManagerId: accountManagerFilter, outreachSpecialistId: outreachSpecialistFilter, category: categoryFilter, publisherOptions, categoryOptions, hideZeroClicks, lastSyncedAt, page, limit, search, sortBy, sortOrder, columnFilters } = useSelector((state) => state.publisherReporting);
    const isCompareActive = Boolean(compareStartDate && compareEndDate);
    // Top cards from the list API summary (comparison + % only when a compare range is selected)
    const summaryCardValues = useMemo(() => {
        const s = summary;
        const pick = (key) => {
            switch (key) {
                case "placements": return { value: s?.cardPlacements, compare: s?.cardComparePlacements, diff: s?.cardPlacementsDiff };
                case "activePublishers": return { value: s?.cardActivePublishers, compare: s?.cardCompareActivePublishers, diff: s?.cardActivePublishersDiff };
                case "revenue": return { value: s?.totalRevenue, compare: s?.totalCompareRevenue, diff: s?.totalRevenueDiff };
                default: return { value: s?.totalSpend, compare: s?.totalCompareSpend, diff: s?.totalSpendDiff };
            }
        };
        return SUMMARY_CARDS.map((card) => {
            const { value, compare, diff } = pick(card.key);
            const showCompare = isCompareActive && s && compare !== undefined;
            const d = diff ?? 0;
            return {
                ...card,
                value: s && value !== undefined ? fmt(value) : "-",
                comparisonValue: showCompare ? fmt(compare) : undefined,
                percentageText: showCompare ? `${Number(Math.abs(d).toFixed(1))} %` : undefined,
                trend: (d > 0 ? "up" : d < 0 ? "down" : "neutral"),
            };
        });
    }, [summary, isCompareActive]);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const allClients = useSelector(selectAllClientList);
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    // The tab lives in redux (it is sent to the API as `type`). Switching starts with a clean sort / column filters.
    // The Funnel filter only exists on By Publisher, so By Client never carries a hidden funnel.
    const setPerformanceView = useCallback((v) => {
        dispatch(setFilters({ view: v, sortBy: "revenue", sortOrder: "desc", columnFilters: {}, ...(v === "client" && { funnel: "All" }) }));
    }, [dispatch]);
    // Toolbar filters live in redux and are sent to the list API (both tabs); changing one goes back to page 1
    const setClientFilter = useCallback((v) => dispatch(setFilters({ clientId: v })), [dispatch]);
    const setAccountManagerFilter = useCallback((v) => dispatch(setFilters({ accountManagerId: v })), [dispatch]);
    const setCategoryFilter = useCallback((v) => dispatch(setFilters({ category: v })), [dispatch]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [detailRow, setDetailRow] = useState(null);
    // Tab the drawer opens on: the count pill opens "breakdown", the Placements count opens "placements"
    const [detailInitialTab, setDetailInitialTab] = useState("breakdown");
    const [addPlacementOpen, setAddPlacementOpen] = useState(false);
    // Bumped after a placement is saved so the open drawer reloads its breakdown + placements
    const [placementsRefresh, setPlacementsRefresh] = useState(0);
    // Placement being duplicated: Add Placement opens pre-filled with it (null = a normal, empty Add Placement)
    const [duplicateSource, setDuplicateSource] = useState(null);
    const [modalSearch, setModalSearch] = useState("");
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [modalSortBy, setModalSortBy] = useState("revenue");
    const [modalSortOrder, setModalSortOrder] = useState("desc");
    // Toolbar states
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnOrder, setColumnOrder] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: ["publisherName"], right: [] });
    const dateValue = useMemo(() => {
        if (!startDate || !endDate)
            return null;
        const [sy, sm, sd] = startDate.split("-").map(Number);
        const [ey, em, ed] = endDate.split("-").map(Number);
        return {
            start: new CalendarDate(sy, sm, sd),
            end: new CalendarDate(ey, em, ed)
        };
    }, [startDate, endDate]);
    const compareDateValue = useMemo(() => {
        if (!compareStartDate || !compareEndDate)
            return null;
        const [csy, csm, csd] = compareStartDate.split("-").map(Number);
        const [cey, cem, ced] = compareEndDate.split("-").map(Number);
        if (!csy || !csm || !csd || !cey || !cem || !ced)
            return null;
        return {
            start: new CalendarDate(csy, csm, csd),
            end: new CalendarDate(cey, cem, ced)
        };
    }, [compareStartDate, compareEndDate]);
    // CommonTable keys selection by row.id - use the group publisher id so selection
    // stays stable across sorting, paging and refetches
    // Both views use different columns / row ids - start clean whenever the view changes
    useEffect(() => {
        setSelectedRows(new Set());
        setHiddenColumns(new Set());
        setColumnOrder(null);
    }, [performanceView]);
    // Never show the other tab's rows while the new tab is loading
    const viewRows = useMemo(() => (rowsView === performanceView ? rows : []), [rows, rowsView, performanceView]);
    const tableRows = useMemo(() => viewRows.map((r) => ({
        ...r,
        id: performanceView === "client" ? (r.client_id || r.clientName) : (r.group_publisher_id || r.publisherName),
    })), [viewRows, performanceView]);
    const publisherDropdownOptions = useMemo(() => [
        { value: "", label: "All" },
        ...(publisherOptions || []).map(name => ({ value: name, label: name }))
    ], [publisherOptions]);
    const categoryDropdownOptions = useMemo(() => [
        { value: "", label: "All" },
        ...(categoryOptions || []).map(name => ({ value: name, label: name }))
    ], [categoryOptions]);
    const userToOption = (u) => ({
        value: u._id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
    });
    // Presentation Mode: only the clients picked for it are listed (the list API is scoped to the same clients)
    const isPresentationMode = useSelector((state) => state.presentation.isPresentationMode);
    const presentationClients = useSelector((state) => state.presentation.presentationClients);
    const clientDropdownOptions = useMemo(() => {
        const presentationIds = new Set((presentationClients || []).map((c) => String(c?._id ?? c)));
        return [
            { value: "", label: "All" },
            ...(allClients || [])
                .filter((c) => c.status === "active" && Array.isArray(c.type) && c.type.includes("affiliate"))
                .filter((c) => !isPresentationMode || presentationIds.has(String(c._id)))
                .map((c) => ({ value: c._id, label: c.name }))
        ];
    }, [allClients, isPresentationMode, presentationClients]);
    const accountManagerOptions = useMemo(() => [
        { value: "", label: "All" },
        ...(accountManagers || []).map(userToOption)
    ], [accountManagers]);
    // Load filter source data once on mount (skipped if already in the store)
    useEffect(() => {
        if (!designationWiseUsers)
            dispatch(fetchDesignationWiseUsers());
        if (!allClients?.length)
            dispatch(fetchAllClientList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);
    const doFetch = useCallback(() => {
        dispatch(fetchPublisherReporting());
    }, [dispatch]);
    useEffect(() => {
        dispatch(fetchReportingFilterOptions());
    }, [dispatch]);
    // Category options follow the list's placements (tab, dates, funnel, publisher group, Client / AM / OS)
    useEffect(() => {
        dispatch(fetchReportingCategories());
    }, [dispatch, performanceView, startDate, endDate, funnel, groupPublisherIds, clientFilter, accountManagerFilter, outreachSpecialistFilter]);
    useEffect(() => {
        doFetch();
    }, [doFetch, performanceView, startDate, endDate, compareStartDate, compareEndDate, funnel, groupPublisherIds, hideZeroClicks, clientFilter, accountManagerFilter, outreachSpecialistFilter, categoryFilter, page, limit, search, sortBy, sortOrder, columnFilters]);
    const handleDateApply = useCallback(({ mainRange, compareRange }) => {
        if (!mainRange)
            return;
        const sDate = `${mainRange.start.year}-${String(mainRange.start.month).padStart(2, "0")}-${String(mainRange.start.day).padStart(2, "0")}`;
        const eDate = `${mainRange.end.year}-${String(mainRange.end.month).padStart(2, "0")}-${String(mainRange.end.day).padStart(2, "0")}`;
        let cStartDate = "";
        let cEndDate = "";
        if (compareRange && compareRange.start && compareRange.end) {
            cStartDate = `${compareRange.start.year}-${String(compareRange.start.month).padStart(2, "0")}-${String(compareRange.start.day).padStart(2, "0")}`;
            cEndDate = `${compareRange.end.year}-${String(compareRange.end.month).padStart(2, "0")}-${String(compareRange.end.day).padStart(2, "0")}`;
        }
        dispatch(setFilters({
            startDate: sDate,
            endDate: eDate,
            compareStartDate: cStartDate,
            compareEndDate: cEndDate,
        }));
    }, [dispatch]);
    const renderDropdownValue = (label, value) => (_jsxs("span", { className: "all-Font-Sizes", children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] }));
    const handleSearchChange = useCallback((v) => {
        dispatch(setFilters({ search: v }));
    }, [dispatch]);
    const handleFilterRowToggle = useCallback(() => {
        setShowFilterRow(prev => !prev);
    }, []);
    const handleColumnVisibilityClick = useCallback((e) => {
        setColumnVisibilityAnchor(e.currentTarget);
    }, []);
    const handleColumnVisibilityChange = useCallback((hidden) => {
        setHiddenColumns(hidden);
    }, []);
    const handleColumnFilterChange = useCallback((colId, val) => {
        dispatch(setFilters({ columnFilters: { ...columnFilters, [colId]: val } }));
    }, [dispatch, columnFilters]);
    const handleColumnOrderChange = useCallback((newOrder) => {
        setColumnOrder(newOrder);
    }, []);
    const renderToolbarFilter = (label, value, options, onChange, sortByLabel = true) => {
        const selectedLabel = value ? options.find(o => o.value === value)?.label : undefined;
        // Nothing selected: "Client: All". Selected: only the value, filter name goes to the tooltip.
        const displayValue = selectedLabel ? (_jsx(HoverTooltip, { title: `${label}: ${selectedLabel}`, placement: "top", useOverlay: false, disableInteractive: true, children: _jsx("span", { className: "all-Font-Sizes", style: { color: "#101828", fontWeight: 500 }, children: truncateName(selectedLabel, 20) }) })) : (_jsx(HoverTooltip, { title: label, placement: "top", useOverlay: false, disableInteractive: true, children: renderDropdownValue(label, "All") }));
        return (_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(Dropdown, { value: value, onChange: (v) => onChange(v), options: options, displayValue: displayValue, leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: true, usePortal: true, sortByLabel: sortByLabel }) }));
    };
    const toolbarFilters = (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }, children: [renderToolbarFilter("Client", clientFilter, clientDropdownOptions, setClientFilter), renderToolbarFilter("Group Publisher", groupPublisherIds[0] || "", publisherDropdownOptions, (v) => dispatch(setFilters({ groupPublisherIds: v ? [v] : [] }))), performanceView === "publisher" && renderToolbarFilter("Funnel", funnel === "All" ? "" : funnel, FUNNEL_FILTER_OPTIONS, (v) => dispatch(setFilters({ funnel: v || "All" })), false), renderToolbarFilter("Account Manager", accountManagerFilter, accountManagerOptions, setAccountManagerFilter), renderToolbarFilter("Category", categoryFilter, categoryDropdownOptions, setCategoryFilter)] }));
    // Export Selected: the API returns the selected rows (all pages) with the current filters / dates / compare range,
    // the Excel file is built from them. Label and enabled state follow the row selection.
    const hasSelection = selectedRows.size > 0;
    const isClientView = performanceView === "client";
    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        if (!hasSelection || exporting)
            return;
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/publisher-reporting/export", {
                type: performanceView,
                ids: Array.from(selectedRows),
                startDate,
                endDate,
                compareStartDate: compareStartDate || "",
                compareEndDate: compareEndDate || "",
                funnel,
                hideZeroClicks,
                groupPublisherIds,
                clientId: clientFilter,
                accountManagerId: accountManagerFilter,
                outreachSpecialistId: outreachSpecialistFilter,
                category: categoryFilter,
                search,
                columnFilters,
                sortBy,
                sortOrder,
            }, { Authorization: token, "x-module-key": "publisher_reporting" });
            const data = response?.data;
            if (!data?.success) {
                toast.error(data?.message || "Failed to export data");
                return;
            }
            exportPublisherReportingExcel(data.data.reporting, data.data.summary, {
                view: performanceView,
                startDate: data.data.startDate,
                endDate: data.data.endDate,
                compareStartDate: data.data.compareStartDate,
                compareEndDate: data.data.compareEndDate,
            });
        }
        catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export data");
        }
        finally {
            setExporting(false);
        }
    };
    const exportSelectedButton = (
    // A disabled button does not fire hover events, so the tooltip sits on a wrapper span
    _jsx(HoverTooltip, { title: hasSelection ? "" : "Select at least one row to export", placement: "top", useOverlay: false, disableInteractive: true, children: _jsx("span", { style: { display: "inline-flex" }, children: _jsx(AppButton, { variant: "contained", 
                // color="primary"
                color: "secondary", className: "app-common-button", size: "small", disabled: !hasSelection, onClick: handleExport, loading: exporting, startIcon: _jsx(CloudDownload, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", flexShrink: 0, fontSize: "13px", fontWeight: 500, gap: 1 }, children: "Export" }) }) }));
    const toggleAction = (_jsxs(Box, { sx: {
            border: "1px solid #EAECF0",
            borderRadius: "8px",
            p: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#fff"
        }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', color: "#667085", fontWeight: 500 }, children: "Show All" }), _jsx(IOSSwitch, { checked: hideZeroClicks, onChange: (e) => dispatch(setFilters({ hideZeroClicks: e.target.checked })) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(InfoTooltip, { title: _jsx(ToggleTooltipContent, {}), placement: "bottom" }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', color: "#667085", fontWeight: 500 }, children: "Hide 0 Clicks" })] })] }));
    const commonRenderHeader = useCallback((column) => {
        const isSorted = sortBy === column.id;
        const direction = isSorted ? sortOrder : "desc";
        return (_jsxs(Box, { sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                gap: 0.5,
                cursor: column.sortable ? "pointer" : "default"
            }, onClick: (e) => {
                if (!column.sortable)
                    return;
                e.stopPropagation();
                const nextOrder = isSorted ? (sortOrder === "asc" ? "desc" : "asc") : "desc";
                dispatch(setFilters({ sortBy: column.id, sortOrder: nextOrder }));
            }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", fontWeight: 700, textTransform: "capitalize", color: "#252b37" }, children: column.label }), column.sortable && (_jsx(Box, { sx: { display: "flex", alignItems: "center", opacity: isSorted ? 1 : 0.2 }, children: direction === "desc" ? (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h4M2.667 8h6M12 7.333v5.334m0 0 2-2m-2 2-2-2m-7.333-6h8", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) : (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h8M2.667 8h6m-6-3.333h4m5.333 4V3.333m0 0 2 2m-2-2-2 2", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) }))] }));
    }, [dispatch, sortBy, sortOrder]);
    const modalRenderHeader = useCallback((column) => {
        const isSorted = modalSortBy === column.id;
        const direction = isSorted ? modalSortOrder : "desc";
        return (_jsxs(Box, { sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                gap: 0.5,
                cursor: column.sortable ? "pointer" : "default"
            }, onClick: (e) => {
                if (!column.sortable)
                    return;
                e.stopPropagation();
                const nextOrder = isSorted ? (modalSortOrder === "asc" ? "desc" : "asc") : "desc";
                setModalSortBy(column.id);
                setModalSortOrder(nextOrder);
            }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", fontWeight: 700, textTransform: "capitalize", color: "#252b37" }, children: column.label }), column.sortable && (_jsx(Box, { sx: { display: "flex", alignItems: "center", opacity: isSorted ? 1 : 0.2 }, children: direction === "desc" ? (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h4M2.667 8h6M12 7.333v5.334m0 0 2-2m-2 2-2-2m-7.333-6h8", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) : (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h8M2.667 8h6m-6-3.333h4m5.333 4V3.333m0 0 2 2m-2-2-2 2", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) }))] }));
    }, [modalSortBy, modalSortOrder]);
    const columns = useMemo(() => [
        { id: "publisherName", label: "Publisher", width: "170px", align: "left", sortable: true, draggable: true, hideable: false, renderHeader: commonRenderHeader },
        { id: "publisherCount", label: "Active Clients", width: "140px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "placements", label: "Placements", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "funnel", label: "Funnel", width: "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "clicks", label: "Clicks", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "revenue", label: "Revenue", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "orders", label: "Orders", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "spend", label: "Spend", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "roas", label: "ROAS", width: isCompareActive ? "140px" : "110px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
    ], [commonRenderHeader, isCompareActive]);
    const detailColumns = useMemo(() => [
        { id: "clientName", label: "Client Name", width: "180px", align: "left", sortable: false, renderHeader: modalRenderHeader },
        { id: "affiliateNetwork", label: "Affiliate Network", width: "150px", align: "left", sortable: false, renderHeader: modalRenderHeader },
        { id: "includedPublisher", label: "Publisher Name", width: "230px", align: "left", sortable: false, renderHeader: modalRenderHeader },
        { id: "clicks", label: "Clicks", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, renderHeader: modalRenderHeader },
        { id: "revenue", label: "Revenue", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, renderHeader: modalRenderHeader },
        { id: "orders", label: "Orders", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, renderHeader: modalRenderHeader },
        { id: "spend", label: "Spend", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, renderHeader: modalRenderHeader },
    ], [modalRenderHeader, isCompareActive]);
    const toAbsoluteUrl = (url) => {
        if (!url)
            return "";
        let clean = url.trim();
        if (clean.includes(" and ")) {
            clean = clean.split(/\s+and\s+/i)[0].trim();
        }
        else if (clean.includes(",")) {
            clean = clean.split(",")[0].trim();
        }
        else if (clean.includes(" ")) {
            clean = clean.split(/\s+/)[0].trim();
        }
        if (!clean)
            return "";
        return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
    };
    const handleLinkClick = (url) => {
        if (url) {
            const absoluteUrl = toAbsoluteUrl(url);
            if (absoluteUrl) {
                window.open(absoluteUrl, "_blank", "noopener,noreferrer");
            }
        }
    };
    // By Client tab: same metric columns as By Publisher, no Funnel
    const clientColumns = useMemo(() => [
        { id: "clientName", label: "Client Name", width: "170px", align: "left", sortable: true, draggable: true, hideable: false, renderHeader: commonRenderHeader },
        { id: "publisherCount", label: "Active Publishers", width: "160px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "placements", label: "Placements", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "clicks", label: "Clicks", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "revenue", label: "Revenue", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "orders", label: "Orders", width: isCompareActive ? "160px" : "120px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "spend", label: "Spend", width: isCompareActive ? "180px" : "150px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
        { id: "roas", label: "ROAS", width: isCompareActive ? "140px" : "110px", align: "left", sortable: true, draggable: true, renderHeader: commonRenderHeader },
    ], [commonRenderHeader, isCompareActive]);
    // Count pill - opens the drawer for the row (same look in both views)
    const renderCountPill = (row) => (_jsx(Box, { sx: { width: "100%", display: "flex", justifyContent: "flex-start" }, children: _jsx(Box, { onClick: () => handleOpenDetail(row), sx: {
                display: "inline-flex",
                px: 1.5,
                py: 0.5,
                borderRadius: "16px",
                bgcolor: "#F0F9FF",
                color: "#026AA2",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "12px",
                "&:hover": {
                    bgcolor: "#D1E9FF"
                }
            }, children: String(row.publisherCount).padStart(2, "0") }) }));
    const renderClientCell = (row, column) => {
        switch (column.id) {
            case "clientName":
                return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#101828", fontWeight: 400, textAlign: "left", width: "100%" }, children: row.clientName });
            case "publisherCount":
                return renderCountPill(row);
            default:
                // clicks / revenue / orders / spend look the same as in the By Publisher table
                return renderCell(row, column);
        }
    };
    const renderCell = (row, column) => {
        switch (column.id) {
            case "publisherName":
                const firstUrl = row.uniqueUrls?.[0];
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-start", width: "100%" }, children: [_jsx(HoverTooltip, { title: row.publisherName, placement: "top", children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                    fontSize: "14px !important",
                                    color: "#101828",
                                    fontWeight: 400,
                                    maxWidth: "130px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: firstUrl ? "pointer" : "default",
                                    "&:hover": {
                                        textDecoration: firstUrl ? "underline" : "none",
                                        color: firstUrl ? "#026AA2" : "#101828"
                                    }
                                }, onClick: () => handleLinkClick(firstUrl), children: row.publisherName }) }), Boolean(firstUrl) && (_jsx(HoverTooltip, { title: "Open Link", placement: "top", children: _jsx(IconButton, { size: "small", sx: { p: 0.5, opacity: 0.6, "&:hover": { opacity: 1 } }, onClick: () => handleLinkClick(firstUrl), children: _jsx("img", { src: "/assets/link.svg", alt: "link", style: { width: "14px", height: "14px" } }) }) }))] }));
            case "publisherCount":
                return (_jsx(Box, { sx: { width: "100%", display: "flex", justifyContent: "flex-start" }, children: _jsx(Box, { onClick: () => handleOpenDetail(row), sx: {
                            display: "inline-flex",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "16px",
                            bgcolor: "#F0F9FF",
                            color: "#026AA2",
                            fontWeight: 600,
                            cursor: "pointer",
                            fontSize: "12px",
                            "&:hover": {
                                bgcolor: "#D1E9FF"
                            }
                        }, children: String(row.publisherCount).padStart(2, "0") }) }));
            case "funnel":
                const funnelKey = row.funnel?.toLowerCase();
                const iconPath = funnelKey === "tof" ? "/assets/tof.svg" :
                    (funnelKey === "mof" || funnelKey === "mod") ? "/assets/mof.svg" :
                        funnelKey === "bof" ? "/assets/bof.svg" : "/assets/funnel.svg";
                const hideIcon = funnelKey === "spam" || funnelKey === "no tag" || funnelKey === "no funnel";
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "100%" }, children: [!hideIcon && _jsx("img", { src: iconPath, alt: row.funnel, style: { width: "14px", height: "14px" } }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#344054", fontWeight: 400 }, children: row.funnel })] }));
            case "placements":
                return (_jsx(MetricCompareCell, { value: row.placements ?? 0, compareValue: isCompareActive ? (row.comparePlacements ?? 0) : undefined, diff: isCompareActive ? (row.placementsDiff ?? 0) : undefined, 
                    // placements > 0: dotted count, click opens the drawer on the Placement Details tab
                    onClick: (row.placements ?? 0) > 0 ? () => handleOpenDetail(row, "placements") : undefined }));
            case "clicks":
                return (_jsx(MetricCompareCell, { value: row.clicks, compareValue: isCompareActive ? row.compareClicks : undefined, diff: isCompareActive ? row.clicksDiff : undefined, isCurrency: false }));
            case "revenue":
                return (_jsx(MetricCompareCell, { value: row.revenue, compareValue: isCompareActive ? row.compareRevenue : undefined, diff: isCompareActive ? row.revenueDiff : undefined, isCurrency: true }));
            case "orders":
                return (_jsx(MetricCompareCell, { value: row.orders, compareValue: isCompareActive ? row.compareOrders : undefined, diff: isCompareActive ? row.ordersDiff : undefined, isCurrency: false }));
            case "spend":
                return (_jsx(MetricCompareCell, { value: row.spend, compareValue: isCompareActive ? row.compareSpend : undefined, diff: isCompareActive ? row.spendDiff : undefined, isCurrency: true }));
            case "roas":
                return (_jsx(MetricCompareCell, { value: row.roas ?? 0, compareValue: isCompareActive ? (row.compareRoas ?? 0) : undefined, diff: isCompareActive ? (row.roasDiff ?? 0) : undefined, isRatio: true }));
            default:
                return null;
        }
    };
    const renderDetailCell = (row, column) => {
        switch (column.id) {
            case "clientName":
                return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#101828", fontWeight: 500, textAlign: "left", width: "100%" }, children: row.clientName });
            case "affiliateNetwork":
                const networkKey = row.affiliateNetwork?.toLowerCase().replace(/[\s_]+/g, "_");
                const iconPath = NETWORK_ICONS[networkKey] || NETWORK_ICONS[row.affiliateNetwork?.toLowerCase()] || "/assets/link.svg";
                return (_jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }, children: _jsx(HoverTooltip, { title: row.affiliateNetwork, placement: "top", children: _jsx(Box, { sx: { display: "flex", alignItems: "center", cursor: "help" }, children: _jsx("img", { src: iconPath, alt: row.affiliateNetwork, style: { width: 20, height: 20, objectFit: "contain" } }) }) }) }));
            case "includedPublisher":
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-start", width: "100%" }, children: [_jsx(HoverTooltip, { title: row.includedPublisher, placement: "top", children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                    fontSize: "14px !important",
                                    color: "#101828",
                                    maxWidth: "180px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: row.publisher_url ? "pointer" : "default",
                                    "&:hover": {
                                        textDecoration: row.publisher_url ? "underline" : "none",
                                        color: row.publisher_url ? "#026AA2" : "#101828"
                                    }
                                }, onClick: () => handleLinkClick(row.publisher_url), children: row.includedPublisher }) }), Boolean(row.publisher_url) && (_jsx(HoverTooltip, { title: "Open Link", placement: "top", children: _jsx(IconButton, { size: "small", sx: { p: 0.5, opacity: 0.6, "&:hover": { opacity: 1 } }, onClick: () => handleLinkClick(row.publisher_url), children: _jsx("img", { src: "/assets/link.svg", alt: "link", style: { width: "14px", height: "14px" } }) }) }))] }));
            case "clicks":
                return (_jsx(MetricCompareCell, { value: row.clicks, compareValue: isCompareActive ? row.compareClicks : undefined, diff: isCompareActive ? row.clicksDiff : undefined, isCurrency: false }));
            case "revenue":
                return (_jsx(MetricCompareCell, { value: row.revenue, compareValue: isCompareActive ? row.compareRevenue : undefined, diff: isCompareActive ? row.revenueDiff : undefined, isCurrency: true }));
            case "orders":
                return (_jsx(MetricCompareCell, { value: row.orders, compareValue: isCompareActive ? row.compareOrders : undefined, diff: isCompareActive ? row.ordersDiff : undefined, isCurrency: false }));
            case "spend":
                return (_jsx(MetricCompareCell, { value: row.spend, compareValue: isCompareActive ? row.compareSpend : undefined, diff: isCompareActive ? row.spendDiff : undefined, isCurrency: true }));
            default:
                return null;
        }
    };
    const renderSummaryRow = (_, column) => {
        if (!summary)
            return null;
        switch (column.id) {
            case "publisherName":
            case "clientName":
                return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 700, textAlign: "left", width: "100%" }, children: "Total Summary" });
            case "placements":
                return (_jsx(MetricCompareCell, { value: summary.totalPlacements ?? 0, compareValue: isCompareActive ? (summary.totalComparePlacements ?? 0) : undefined, diff: isCompareActive ? (summary.totalPlacementsDiff ?? 0) : undefined, bold: true }));
            case "clicks":
                return (_jsx(MetricCompareCell, { value: summary.totalClicks, compareValue: isCompareActive ? summary.totalCompareClicks : undefined, diff: isCompareActive ? summary.totalClicksDiff : undefined, isCurrency: false, bold: true }));
            case "revenue":
                return (_jsx(MetricCompareCell, { value: summary.totalRevenue, compareValue: isCompareActive ? summary.totalCompareRevenue : undefined, diff: isCompareActive ? summary.totalRevenueDiff : undefined, isCurrency: true, bold: true }));
            case "orders":
                return (_jsx(MetricCompareCell, { value: summary.totalOrders, compareValue: isCompareActive ? summary.totalCompareOrders : undefined, diff: isCompareActive ? summary.totalOrdersDiff : undefined, isCurrency: false, bold: true }));
            case "spend":
                return (_jsx(MetricCompareCell, { value: summary.totalSpend, compareValue: isCompareActive ? summary.totalCompareSpend : undefined, diff: isCompareActive ? summary.totalSpendDiff : undefined, isCurrency: true, bold: true }));
            case "roas":
                return (_jsx(MetricCompareCell, { value: summary.totalRoas ?? 0, compareValue: isCompareActive ? (summary.totalCompareRoas ?? 0) : undefined, diff: isCompareActive ? (summary.totalRoasDiff ?? 0) : undefined, isRatio: true, bold: true }));
            default:
                return null;
        }
    };
    const sortedDetailRows = useMemo(() => {
        const q = modalSearch.toLowerCase();
        const filtered = (detailRow?.detail || []).filter((d) => d.clientName?.toLowerCase().includes(q) ||
            d.affiliateNetwork?.toLowerCase().includes(q) ||
            d.includedPublisher?.toLowerCase().includes(q));
        return [...filtered].sort((a, b) => {
            const av = a[modalSortBy] || 0;
            const bv = b[modalSortBy] || 0;
            return modalSortOrder === "asc" ? av - bv : bv - av;
        });
    }, [detailRow, modalSearch, modalSortBy, modalSortOrder]);
    const modalUrls = useMemo(() => {
        const urls = new Set();
        (detailRow?.uniqueUrls || []).forEach((u) => {
            if (!u)
                return;
            const parts = u.split(/\s+and\s+|,|;|\n|\r/i).map((s) => s.trim()).filter(Boolean);
            parts.forEach((p) => urls.add(p));
        });
        return Array.from(urls);
    }, [detailRow?.uniqueUrls]);
    const handleOpenDetail = (row, tab = "breakdown") => {
        setModalSearch("");
        setModalSortBy("revenue");
        setModalSortOrder("desc");
        setDetailInitialTab(tab);
        setDetailRow(row);
    };
    const handleCloseModal = () => {
        setAddPlacementOpen(false);
        setDetailRow(null);
        setIsFullScreen(false);
    };
    return (_jsxs(PageContainer, { children: [_jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "12px", bgcolor: "transparent" }, children: [_jsx(PagesCommonHeader, { height: "auto", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastSyncedAt, onRefresh: doFetch, loading: loading, moduleName: "publisher_reporting", showIcon: false }), rightSection: _jsxs(Box, { sx: { display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }, children: [_jsx(CustomDatePicker, { value: dateValue, compareValue: compareDateValue, onApply: handleDateApply, comparison: true }), canManagePlacements && (_jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", size: "small", 
                                    // not opened from a row: no publisher / client pre-selected, not a duplicate
                                    onClick: () => { setDuplicateSource(null); setAddPlacementOpen(true); }, startIcon: _jsx(Plus, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 500 }, children: "Add Placement" }))] }) }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, width: "100%" }, children: summaryCardValues.map((card) => (_jsx(MetricCard, { title: card.title, icon: card.icon, tooltipText: card.tooltipText, value: card.value, prefix: card.prefix, comparisonValue: card.comparisonValue, comparisonPrefix: card.prefix, percentageText: card.percentageText, trend: card.trend, sx: { p: 2 } }, card.title))) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", px: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "18px !important", fontWeight: 700, color: "#101828" }, children: PERFORMANCE_TITLES[performanceView] }), _jsx(InfoTooltip, { title: performanceView === "publisher"
                                            ? "Performance of each publisher in the selected date range."
                                            : "Performance of each client in the selected date range.", placement: "top" })] }), _jsx(CommonTabs, { selectedValue: performanceView, onChange: setPerformanceView, tabs: PERFORMANCE_TABS })] }), _jsxs(Box, { sx: { bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }, children: [_jsx(Box, { className: "publisher-reporting-toolbar", sx: {
                                    bgcolor: "#fff",
                                    px: 1,
                                    py: 0,
                                    width: "100%",
                                    // Filters live in the title slot (left) - let it take the free width
                                    // and keep the icon group + Export (right) at their natural size
                                    "& .table-toolbar-title-section": { maxWidth: "none", minWidth: 0, justifyContent: "flex-start" },
                                    "& .table-toolbar-title": { whiteSpace: "normal", minWidth: 0 },
                                    "& .table-toolbar-actions": { flex: "0 0 auto" },
                                }, children: _jsx(TableToolbar, { title: toolbarFilters, showSearch: true, showExpand: true, showFullScreen: true, showRows: true, enableColumnVisibility: true, enableColumnFilters: true, showFilterRow: showFilterRow, searchValue: search, onFilterRowToggle: handleFilterRowToggle, onColumnVisibilityClick: handleColumnVisibilityClick, onSearchChange: handleSearchChange, 
                                    // customActionsLeft={exportSelectedButton}
                                    customActions: exportSelectedButton, searchPlaceholder: isClientView ? "Search clients..." : "Search publishers..." }) }), _jsxs(Box, { sx: { bgcolor: "#fff", overflow: "hidden" }, children: [_jsx(CommonTable
                                    // Both tabs load from the same API (`type` = tab); the key resets table state on switch
                                    , { columns: isClientView ? clientColumns : columns, rows: tableRows, renderCell: isClientView ? renderClientCell : renderCell, isLoading: loading, isFirstLoad: loading && tableRows.length === 0, 
                                        // Row selection
                                        enableSelection: true, selectedRows: selectedRows, onSelectionChange: setSelectedRows, 
                                        // no rows ("No data available") = no Total Summary footer either
                                        enableSummaryRow: tableRows.length > 0, renderSummaryRow: renderSummaryRow, summaryRowPosition: "bottom", sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (colId, dir) => dispatch(setFilters({ sortBy: colId, sortOrder: dir || "desc" })), expandTrigger: "none", stickyHeader: true, maxHeight: "calc(100vh - 220px)", 
                                        // Visibility
                                        columnVisibilityConfig: { enable: true, enableReorder: true }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), 
                                        // Pinning
                                        enableColumnPinning: true, pinnedColumns: isClientView ? { left: ["clientName"], right: [] } : pinnedColumns, onColumnPinningChange: setPinnedColumns, 
                                        // Reorder
                                        enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, 
                                        // Filters
                                        enableColumnFilters: true, showFilterRow: showFilterRow, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, columnFilterConfig: {
                                            enable: true,
                                            columns: isClientView
                                                ? { clientName: { type: "text" } }
                                                : {
                                                    publisherName: { type: "text" },
                                                    funnel: { type: "dropdown", options: TAG_FILTER_OPTIONS }
                                                }
                                        } }, performanceView), _jsx(Box, { sx: { p: "12px 16px", borderTop: "1px solid #E5E7Eb" }, children: _jsx(Pagination, { currentPage: page, totalPages: Math.ceil(total / (limit || 20)), totalEntries: total, itemsPerPage: limit, onPageChange: (p) => dispatch(setPagination({ page: p })) }) })] })] })] }), _jsx(ActiveClientsDrawer, { open: Boolean(detailRow), onClose: handleCloseModal, view: performanceView, rowId: isClientView ? detailRow?.client_id : (detailRow?.group_publisher_id || detailRow?.publisherName), name: isClientView ? detailRow?.clientName : detailRow?.publisherName, startDate: startDate, endDate: endDate, compareStartDate: compareStartDate, compareEndDate: compareEndDate, onAddPlacement: () => { setDuplicateSource(null); setAddPlacementOpen(true); }, onDuplicatePlacement: (placement) => { setDuplicateSource(placement); setAddPlacementOpen(true); }, onPlacementDeleted: () => {
                    setPlacementsRefresh((k) => k + 1);
                    // the list rows and cards carry the placement counts too
                    doFetch();
                }, initialTab: detailInitialTab, refreshKey: placementsRefresh, clientFilter: clientFilter, accountManagerFilter: accountManagerFilter, outreachSpecialistFilter: outreachSpecialistFilter, categoryFilter: categoryFilter }), _jsx(AddPlacementDrawer, { open: addPlacementOpen, onClose: () => setAddPlacementOpen(false), onSaved: () => {
                    setPlacementsRefresh((k) => k + 1);
                    // the list rows and cards carry the placement counts too
                    doFetch();
                }, publisherGroupId: isClientView ? undefined : detailRow?.group_publisher_id, publisherName: isClientView ? undefined : detailRow?.publisherName, clientId: isClientView ? detailRow?.client_id : undefined, clientName: isClientView ? detailRow?.clientName : undefined, duplicateFrom: duplicateSource })] }));
};
export default PublisherReportingPage;
